import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Ticket, TrendingUp, Trophy, Calendar, User, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [regs, setRegs] = useState([]);
  const [standings, setStandings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [avatar, setAvatar] = useState(user?.picture || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/my/registrations").then((r) => setRegs(r.data)).catch(() => {});
    api.get("/my/standings").then((r) => setStandings(r.data)).catch(() => {});
    api.get("/my/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setAvatar(user?.picture || ""); }, [user]);

  const uploadAvatar = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/my/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.url);
      toast.success("Profile photo updated!");
      if (refresh) refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const avatarSrc = avatar ? (avatar.startsWith("http") ? avatar : `${process.env.REACT_APP_BACKEND_URL}${avatar}`) : "";

  const handleCancel = async (reg) => {
    if (!window.confirm(`Cancel your seat for "${reg.event_title}" ?`)) return;
    try {
      await api.post(`/my/registrations/${reg.id}/cancel`);
      toast.success("Seat cancelled.");
      const r = await api.get("/my/registrations");
      setRegs(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel this seat");
    }
  };

  const totalPoints = standings.reduce((a, s) => a + (s.points || 0), 0);
  const wins = standings.filter((s) => s.place === 1).length;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14" data-testid="dashboard-page">
      <div className="mb-10 flex items-center gap-5">
        <div className="relative group">
          <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gold bg-navy-paper flex items-center justify-center" data-testid="dashboard-avatar">
            {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : <User className="w-9 h-9 text-gold" />}
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="absolute -bottom-1 -right-1 bg-gold text-navy p-1.5 rounded-full hover:bg-gold-light transition-colors" data-testid="avatar-upload-btn" title="Change photo">
            <Camera className="w-3.5 h-3.5" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="avatar-input" />
        </div>
        <div>
          <div className="text-gold text-xs uppercase tracking-[0.35em] mb-2">My Legends</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mb-12">
        {[
          { icon: TrendingUp, label: "Season Points", value: totalPoints },
          { icon: Trophy, label: "Event Wins", value: wins },
          { icon: Ticket, label: "Seats Booked", value: regs.length },
        ].map((s, i) => (
          <div key={s.label} className="bg-navy-paper border border-slate-800 p-6" data-testid={`stat-${i}`}>
            <s.icon className="w-7 h-7 text-gold mb-3" />
            <div className="font-display text-3xl font-bold text-white">{s.value}</div>
            <div className="text-slate-400 text-xs uppercase tracking-widest mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-bold text-white mb-4">My Seats</h2>
      <div className="border border-white/10 mb-12 overflow-x-auto">
        <table className="w-full text-left min-w-[560px]" data-testid="my-registrations">
          <thead className="bg-secondary text-gold text-xs uppercase tracking-wider">
            <tr><th className="px-5 py-3">Event</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Booked</th><th className="px-5 py-3">Action</th></tr>
          </thead>
          <tbody>
            {regs.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="px-5 py-3 text-white">{r.event_title}</td>
                <td className="px-5 py-3">
                  <Badge className={`rounded-none ${["paid", "registered"].includes(r.payment_status) ? "bg-accent text-white" : "bg-secondary text-slate-400"}`} data-testid={`seat-status-${r.id}`}>
                    {["paid", "registered"].includes(r.payment_status) ? "Registered" : ["refunded", "cancelled"].includes(r.payment_status) ? "Cancelled" : "Pending"}
                  </Badge>
                </td>
                <td className="px-5 py-3 text-slate-500 text-sm">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3">
                  {["paid", "registered"].includes(r.payment_status)
                    ? <button onClick={() => handleCancel(r)} className="text-slate-400 hover:text-destructive text-xs underline" data-testid={`cancel-seat-${r.id}`}>Cancel seat</button>
                    : <span className="text-slate-600 text-xs">—</span>}
                </td>
              </tr>
            ))}
            {regs.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                No seats yet. <Link to="/events" className="text-gold underline">Browse events</Link> to book one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {waitlist.length > 0 && (
        <div className="mb-12" data-testid="my-waitlist">
          <h2 className="font-display text-xl font-bold text-white mb-4">My Waitlists</h2>
          <div className="flex flex-wrap gap-3">
            {waitlist.map((w) => (
              <Link key={w.id} to={`/events/${w.event_id}`} className="bg-navy-paper border border-slate-800 hover:border-gold/50 transition-colors px-4 py-3 text-sm">
                <span className="text-white">{w.event_title}</span>
                <span className={`ml-3 text-xs ${w.notified ? "text-accent" : "text-gold"}`}>{w.notified ? "Seat available!" : `You're #${w.position}`}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white">My Results</h2>
        <Link to="/leaderboards"><Button variant="outline" className="rounded-none border-gold/40 text-gold hover:bg-gold hover:text-navy text-xs uppercase tracking-widest">View Leaderboards</Button></Link>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-left min-w-[560px]" data-testid="my-standings">
          <thead className="bg-secondary text-gold text-xs uppercase tracking-wider">
            <tr><th className="px-5 py-3">Event</th><th className="px-5 py-3">Venue</th><th className="px-5 py-3">Place</th><th className="px-5 py-3 text-right">Points</th></tr>
          </thead>
          <tbody>
            {standings.map((s) => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="px-5 py-3 text-white text-sm">{s.event_title}</td>
                <td className="px-5 py-3 text-slate-400 text-sm">{s.venue}</td>
                <td className="px-5 py-3 text-gold font-bold">{s.place}</td>
                <td className="px-5 py-3 text-right text-slate-300">{s.points}</td>
              </tr>
            ))}
            {standings.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-slate-500 flex-col">
                <Calendar className="w-6 h-6 mx-auto mb-2 opacity-40" /> No results recorded yet — play an event to get on the board!
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
